package fr.frux.feature.favorites

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.KeyboardArrowRight
import androidx.compose.material.icons.outlined.FavoriteBorder
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.Dp
import fr.frux.app.resources.Res
import fr.frux.app.resources.favorites_add_label
import fr.frux.app.resources.favorites_empty_cta
import fr.frux.app.resources.favorites_empty_message
import fr.frux.app.resources.favorites_empty_title
import fr.frux.app.resources.favorites_remove_label
import fr.frux.app.resources.favorites_sites_title
import fr.frux.app.resources.favorites_title
import fr.frux.core.designsystem.components.EmptyState
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.FruxFavoriteButton
import fr.frux.core.designsystem.components.FruxMinTouchTarget
import fr.frux.core.designsystem.components.FruxScreenHeader
import fr.frux.core.designsystem.components.LoadingState
import fr.frux.core.designsystem.components.LocalBottomNavPadding
import fr.frux.core.designsystem.components.SnackbarMessageEffect
import fr.frux.core.designsystem.components.TreeCard
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.icons.treeMarkerTint
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.site.labelRes
import fr.frux.shared.domain.model.Site
import org.jetbrains.compose.resources.stringResource

/**
 * Favoris — point de retour terrain : « où est le prunier que j'ai repéré au printemps ».
 *
 * @param onExploreMap CTA de l'état vide, renvoie vers la carte.
 * @param onTreeClick Ouvre la fiche d'un arbre.
 * @param onSiteClick Ouvre la fiche d'un lieu suivi.
 */
@Composable
fun FavoritesScreen(
    onExploreMap: () -> Unit,
    onTreeClick: (Long) -> Unit,
    onSiteClick: (Long) -> Unit,
    viewModel: FavoritesViewModel = rememberFavoritesViewModel(),
) {
    val state by viewModel.state.collectAsState()
    val snackbarHostState = remember { SnackbarHostState() }
    val bottomNavPadding = LocalBottomNavPadding.current

    // Retour sur cet écran depuis une fiche poussée par-dessus (retrait du favori depuis le
    // cœur de la fiche, cf. constat #1 de la revue favoris) : le ViewModel est scopé à la
    // NavEntry et survit à ce push/pop (seul un changement d'onglet le détruit), mais l'entrée
    // de composition, elle, est démontée puis remontée par Nav3 — donc ce LaunchedEffect(Unit)
    // repart de zéro à chaque retour et recharge une liste qui aurait pu changer ailleurs.
    // Contrairement à la carte (surface permanente, cf. ADR 0009), l'effet ici ne se déclenche
    // qu'aux vraies navigations (montage initial + retour de push), jamais en boucle : pas
    // besoin d'une garde de péremption, un rechargement inconditionnel reste simple et correct.
    LaunchedEffect(viewModel) {
        viewModel.load()
    }

    // Le message ponctuel vit dans l'état du VM, pas dans un effet d'écran : l'entryProvider de
    // Nav3 est recréé à chaque changement de pile et annulerait un one-shot local (cf. #145).
    SnackbarMessageEffect(
        trigger = state.transientError,
        message = state.transientError?.userMessage(),
        snackbarHostState = snackbarHostState,
    ) { viewModel.onErrorShown() }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = MaterialTheme.colorScheme.background,
    ) { paddingValues ->
        // Le sous-titre promesse du mock reste dans l'état vide (message CTA existant, cf.
        // EmptyState ci-dessous) plutôt que remonté dans le header : il est spécifique à
        // « pas encore de favori » et n'a pas de sens quand la liste est pleine.
        Column(modifier = Modifier.fillMaxSize().padding(paddingValues)) {
            FruxScreenHeader(title = stringResource(Res.string.favorites_title))
            Box(modifier = Modifier.weight(1f)) {
                when {
                    // Padding de barre du bas sur les quatre branches (constat #5 de la revue
                    // favoris) : sans lui, le bouton « Réessayer » de l'état erreur pouvait passer
                    // sous la barre — exactement quand l'utilisateur en a besoin.
                    state.isLoading -> {
                        LoadingState(
                            modifier = Modifier.padding(bottom = bottomNavPadding),
                        )
                    }

                    // Des lieux suivis chargés restent visibles : l'erreur des arbres passe sous eux.
                    state.error != null && state.favoriteSites.isEmpty() -> {
                        ErrorState(
                            message = state.error!!.userMessage(),
                            onRetry = viewModel::load,
                            modifier = Modifier.padding(bottom = bottomNavPadding),
                        )
                    }

                    state.trees.isEmpty() && state.favoriteSites.isEmpty() -> {
                        EmptyState(
                            icon = Icons.Outlined.FavoriteBorder,
                            title = stringResource(Res.string.favorites_empty_title),
                            message = stringResource(Res.string.favorites_empty_message),
                            ctaLabel = stringResource(Res.string.favorites_empty_cta),
                            onCta = onExploreMap,
                            modifier = Modifier.padding(bottom = bottomNavPadding),
                        )
                    }

                    else -> {
                        FavoritesList(
                            state = state,
                            bottomPadding = bottomNavPadding,
                            onTreeClick = onTreeClick,
                            onSiteClick = onSiteClick,
                            onToggleFavorite = viewModel::onToggleFavorite,
                            onRetry = viewModel::load,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun FavoritesList(
    state: FavoritesState,
    bottomPadding: Dp,
    onTreeClick: (Long) -> Unit,
    onSiteClick: (Long) -> Unit,
    onToggleFavorite: (Long) -> Unit,
    onRetry: () -> Unit,
) {
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding =
            PaddingValues(
                start = FruxSpacing.md,
                end = FruxSpacing.md,
                top = FruxSpacing.md,
                bottom = bottomPadding,
            ),
        verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
    ) {
        // Absente sans lieu suivi : l'écran reste celui des arbres pour qui ne suit aucun lieu.
        if (state.favoriteSites.isNotEmpty()) {
            item(key = "sites-title") {
                Text(
                    text = stringResource(Res.string.favorites_sites_title),
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.semantics { heading() },
                )
            }
            items(state.favoriteSites, key = { "site-${it.id}" }) { site ->
                FollowedSiteRow(site, onClick = { onSiteClick(site.id) })
            }
        }
        state.error?.let { error ->
            item(key = "trees-error") { ErrorState(message = error.userMessage(), onRetry = onRetry) }
        }
        items(state.trees, key = { it.id }) { tree ->
            TreeCard(
                name = tree.name,
                meta = tree.meta,
                status = tree.status,
                onClick = { onTreeClick(tree.id) },
                glyph = tree.glyph,
                glyphTint = treeMarkerTint(tree.scientificName, tree.status),
                thumbUrl = tree.photoUrl,
                trailing = {
                    FruxFavoriteButton(
                        isFavorite = tree.isFavorite,
                        onClick = { onToggleFavorite(tree.id) },
                        addLabel = stringResource(Res.string.favorites_add_label, tree.name),
                        removeLabel = stringResource(Res.string.favorites_remove_label, tree.name),
                    )
                },
            )
        }
    }
}

/** Un lieu suivi : son nom et sa règle d'accès. */
@Composable
private fun FollowedSiteRow(
    site: Site,
    onClick: () -> Unit,
) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .heightIn(min = FruxMinTouchTarget)
                .clickable(onClick = onClick)
                .padding(vertical = FruxSpacing.xs),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(text = site.name, style = MaterialTheme.typography.bodyLarge)
            Text(
                text = stringResource(site.accessRule.labelRes()),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        Icon(
            imageVector = Icons.AutoMirrored.Outlined.KeyboardArrowRight,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}
